// Typed wrappers around /api/v1/goal-alignment/* — the single point of
// contact between the SPA and the FastAPI Goal-Based Risk Alignment
// surface.
//
// Mirrors the conventions used by features/portfolio-stress-test/api
// and features/risk-assessment/api: reuse the shared apiClient (which
// attaches the JWT and centralises 401 handling), keep wire types
// next to the wrappers so backend contract drift is loud at compile
// time.

import { apiClient } from '../../../api/client';

/** Map of scenario name -> { short asset key -> percent return }. The
 *  backend has already translated the canonical labels through ASSET_MAP
 *  so the keys ('equity', 'crypto', ...) match the simulation engine. */
export type GoalScenarioCatalogue = Record<string, Record<string, number>>;

export interface GoalScenariosResponse {
    scenarios: GoalScenarioCatalogue;
}

export type ShockMode = 'none' | 'every_year' | 'shock_year' | 'random';

export type FeedbackLevel = 'strong' | 'on-track' | 'at-risk';

export interface FeedbackMessage {
    level: FeedbackLevel;
    headline: string;
    message: string;
}

export interface HistogramBin {
    lower: number;
    upper: number;
    /** Compact human label for the bin midpoint, e.g. "$120k". */
    label: string;
    count: number;
}

export interface RiskSnapshot {
    probability_of_success: number;
    worst_case_loss: number;
    median: number;
    p10: number;
}

/** One realised outcome distribution — either the gross (Before-Tax) or
 *  the net (After-Tax) path. Both share the same market shocks so the two
 *  blocks in a response are directly comparable. */
export interface SimulationMetrics {
    median: number;
    p10: number;
    p90: number;
    probability_of_success: number;
    worst_case_loss: number;
    histogram: HistogramBin[];
}

/** Request body for POST /goal-alignment/goal-simulation. */
export interface GoalSimulationRequest {
    /** Allocation keyed by canonical asset-class name OR short backend
     *  key. Values are renormalised internally to sum to 1.0, so the
     *  SPA can forward the persisted recommended allocation as-is. */
    allocation: Record<string, number>;
    monthly_contribution: number;
    years: number;
    target: number;
    /** ``null`` (or omitted) for the no-shock base case; ``'random'``
     *  to sample one scenario per iteration; otherwise one of the keys
     *  from GET /scenarios. */
    scenario?: string | null;
    shock_mode?: ShockMode;
    iterations?: number;
    seed?: number | null;
    // --- Advanced (dual-path gross vs net) inputs ---
    initial_investment?: number;
    trading_fee_pct?: number;
    dividend_yield_pct?: number;
    dividend_period?: string;
    capital_gain_tax_pct?: number;
}

/** Response body for POST /goal-alignment/goal-simulation. */
export interface GoalSimulationResponse {
    median: number;
    p10: number;
    p90: number;
    /** 0..1. Frontend multiplies by 100 for display. */
    probability_of_success: number;
    target: number;
    iterations: number;
    years: number;
    initial_investment: number;
    monthly_contribution: number;
    /** Sum of all monthly contributions (plus the initial investment)
     *  across the horizon, useful for framing the worst-case loss vs
     *  what the user actually paid in. */
    total_contributed: number;
    /** Echo of the scenario actually simulated (``null`` for base case,
     *  ``'random'`` for catalogue-sampling). */
    scenario: string | null;
    shock_mode: string;
    /** When ``shock_mode === 'shock_year'``, the median year the shock
     *  fired across the iterations (1-indexed). ``null`` otherwise. */
    shock_year: number | null;
    histogram: HistogramBin[];
    feedback: FeedbackMessage;
    snapshot: RiskSnapshot;
    // --- Dual-path breakdown ---
    before_tax: SimulationMetrics;
    after_tax: SimulationMetrics;
}

/** Request body for POST /goal-alignment/scenario-comparison. Inherits
 *  every simulation knob and adds the list of scenarios to evaluate. */
export interface ScenarioComparisonRequest
    extends Omit<GoalSimulationRequest, 'scenario'> {
    /** Use ``null`` (or empty string) for the no-shock base case. */
    scenarios: (string | null)[];
}

export interface ScenarioComparisonRow {
    scenario: string;
    probability_of_success?: number;
    median?: number;
    p10?: number;
    p90?: number;
    feedback_level?: FeedbackLevel;
    error?: string;
    // --- Dual-path breakdown ---
    before_tax?: SimulationMetrics;
    after_tax?: SimulationMetrics;
}

export interface ScenarioComparisonResponse {
    rows: ScenarioComparisonRow[];
}

export const goalAlignmentApi = {
    /** GET /goal-alignment/scenarios — catalogue for the dropdown. */
    async getScenarios(): Promise<GoalScenariosResponse> {
        const { data } = await apiClient.get<GoalScenariosResponse>(
            '/goal-alignment/scenarios',
        );
        return data;
    },

    /** POST /goal-alignment/goal-simulation — run the Monte Carlo. */
    async runSimulation(
        payload: GoalSimulationRequest,
    ): Promise<GoalSimulationResponse> {
        const { data } = await apiClient.post<GoalSimulationResponse>(
            '/goal-alignment/goal-simulation',
            payload,
        );
        return data;
    },

    /** POST /goal-alignment/scenario-comparison — side-by-side rows. */
    async compareScenarios(
        payload: ScenarioComparisonRequest,
    ): Promise<ScenarioComparisonResponse> {
        const { data } = await apiClient.post<ScenarioComparisonResponse>(
            '/goal-alignment/scenario-comparison',
            payload,
        );
        return data;
    },
};
